/* eslint-disable indent */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { useBasePath, useSettings, useOrganizations, useProperties } from 'hooks';

import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, TextField, Grid, Button, Divider } from '@material-ui/core';
import _ from 'lodash';
import { DashboardHelpers } from 'utils';
import { DeleteConfirmPrompt } from 'components';
import { Modal } from '@material-ui/core';
import palette from 'theme/palette';
import { AuthContext } from 'context/AuthContext';
import { useContext } from 'react';

const useStyles = makeStyles((theme) => ({
  root: { overflow: 'visible' },
  alert: {
    marginBottom: theme.spacing(3),
  },
  formGroup: {
    marginBottom: theme.spacing(3),
    width: '100%',
    flexGrow: 1,
  },
  formGroupGrid: {
    width: '100%',
    flexGrow: 1,
    marginBottom: theme.spacing(3),
  },
  fieldGroup: {
    display: 'flex',
    alignItems: 'center',
  },
  fieldHint: {
    margin: theme.spacing(1, 0),
  },
  addButton: {
    marginLeft: theme.spacing(1),
  },
  tags: {
    marginTop: theme.spacing(1),
    '& > * + *': {
      marginLeft: theme.spacing(1),
    },
  },
  flexGrow: {
    flexGrow: 1,
  },
}));

const PropertyForm = ({ className, errors, property, onSave, onDelete, properties, ...rest }) => {
  const { selectedOrg } = useSettings();
  const { selectedOrganization } = useOrganizations();
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const [values, setValues] = useState(
    property
      ? {
          name: property.name,
          type: property.type,
          id: property.id,
          description: property.description,
          organization_id: property.organization_id,
        }
      : {
          name: '',
          type: '',
          description: '',
          organization_id: selectedOrganization ? selectedOrganization.id : selectedOrg.id,
        }
  );
  const [propertyFormErrors, setPropertyFormErrors] = useState({ name: '', type: '' });
  const [showPropertyDeleteModal, setShowPropertyDeleteModal] = useState(false);

  const { p } = useBasePath();
  const { user } = useContext(AuthContext);
  const [userId, setUserId] = useState();
  const { createUserProperty } = useProperties();

  useEffect(() => {
    if (user?.id) {
      setUserId(user?.id);
    }
  }, []);

  useEffect(() => {
    if (property) {
      setValues({
        name: property.name,
        type: property.type,
        id: property.id,
        description: property.description,
        organization_id: property.organization_id,
      });
    }
  }, [property]);

  const handleFieldChange = (event, field, value) => {
    if (event.persist) {
      setValues({ ...values, [field]: value });
    }
  };

  const validatePropertyForm = () => {
    let formErrorBuilder = propertyFormErrors;

    if (!property) {
      formErrorBuilder = DashboardHelpers.validateFormField(
        values,
        'name',
        formErrorBuilder,
        t('property_name_required')
      );
    }

    formErrorBuilder = DashboardHelpers.validateFormField(
      values,
      'type',
      formErrorBuilder,
      t('property_type_required')
    );

    setPropertyFormErrors({ ...formErrorBuilder });
    return _.filter(formErrorBuilder, (val) => {
      return val !== '';
    }).length > 0
      ? false
      : true;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    let existingProperty = _.find(properties, { name: values.name });
    if (existingProperty && values.id === undefined) {
      setPropertyFormErrors({ name: t('error_unique_property_name') });
    } else if (validatePropertyForm()) {
      let valuesCheck = values;

      if (valuesCheck['organization_id'] === undefined || valuesCheck['organization_id'] === '') {
        valuesCheck = {
          ...valuesCheck,
          ['organization_id']: selectedOrganization ? selectedOrganization.id : selectedOrg.id,
        };
      }
      const result = await onSave(valuesCheck);
      if (result) {
        if (userId && userId.length > 0) {
          createUserProperty({ user_id: userId, property_id: result.payload.id });
        }
        history.push(p('/properties'));
      }
    }
  };

  const handleDelete = async () => {
    if (validatePropertyForm()) {
      let valuesCheck = values;

      if (valuesCheck['organization_id'] === undefined || valuesCheck['organization_id'] === '') {
        valuesCheck = {
          ...valuesCheck,
          ['organization_id']: selectedOrganization ? selectedOrganization.id : selectedOrg.id,
        };
      }
      await onDelete(valuesCheck);
      history.push(p('/properties'));
    }
  };

  const handleConfirm = async () => {
    setShowPropertyDeleteModal(true);
  };

  const lostFocus = (event, field) => {
    let formErrorBuilder = propertyFormErrors;
    const realTimeFormData = { [field]: event.target.value };

    formErrorBuilder = DashboardHelpers.validateFormField(
      realTimeFormData,
      field,
      formErrorBuilder,
      t('property_' + field + '_required')
    );

    setPropertyFormErrors({ ...formErrorBuilder });
  };

  const formIsValid = () => {
    return values.name !== '' && propertyFormErrors.name === '' && values.type !== '' && propertyFormErrors.type === '';
  };

  return (
    <Card {...rest} className={clsx(classes.root, className)}>
      <CardContent>
        <Modal
          open={showPropertyDeleteModal}
          onClose={() => {}}
          aria-labelledby="modal-organization-edit"
          aria-describedby="modal-organization-edit-form"
        >
          <div>
            <DeleteConfirmPrompt
              show={showPropertyDeleteModal}
              data={property}
              deleteConfirm={handleDelete}
              hide={() => setShowPropertyDeleteModal(false)}
            />
          </div>
        </Modal>
        <form
          onSubmit={() => {
            handleSave();
          }}
        >
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} sm={6}>
              <div className={classes.formGroupGrid}>
                <TextField
                  onBlur={(event) => {
                    lostFocus(event, 'name');
                  }}
                  fullWidth
                  label={t('property_name')}
                  name="name"
                  autoComplete="name"
                  onChange={(event) => handleFieldChange(event, 'name', event.target.value)}
                  value={values.name}
                  placeholder={t('property_name')}
                  helperText={propertyFormErrors.name}
                  error={propertyFormErrors.name !== ''}
                  inputProps={{
                    maxLength: 24,
                  }}
                  required
                />
              </div>
              <div className={classes.formGroupGrid}>
                <TextField
                  onBlur={(event) => {
                    lostFocus(event, 'type');
                  }}
                  fullWidth
                  label={t('property_type')}
                  name="type"
                  autoComplete="type"
                  onChange={(event) => handleFieldChange(event, 'type', event.target.value)}
                  value={values && values.type}
                  placeholder={t('property_type')}
                  helperText={propertyFormErrors.type}
                  error={propertyFormErrors.type !== ''}
                  inputProps={{
                    maxLength: 24,
                  }}
                  required
                />
              </div>
              <div className={classes.formGroupGrid}>
                <TextField
                  fullWidth
                  label={t('property_description')}
                  name="description"
                  autoComplete="description"
                  onChange={(event) => handleFieldChange(event, 'description', event.target.value)}
                  value={values && values.description}
                  placeholder={t('property_description')}
                  helperText={errors && errors.givenDescription}
                  error={!!(errors && errors.givenDescription)}
                  inputProps={{
                    maxLength: 144,
                  }}
                  multiline
                />
              </div>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Button
                style={{ marginRight: 24 }}
                onClick={() => {
                  history.push(p('/properties'));
                }}
                variant="outlined"
              >
                {t('cancel')}
              </Button>
              <Button
                style={{ marginRight: 24 }}
                disabled={!formIsValid()}
                onClick={handleSave}
                variant="contained"
                color="primary"
              >
                {t('save')}
              </Button>
              {values.id !== undefined && (
                <Button
                  onClick={handleConfirm}
                  variant="outlined"
                  style={{ backgroundColor: palette.error.main, color: 'white' }}
                >
                  {t('delete')}
                </Button>
              )}
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

PropertyForm.propTypes = {
  className: PropTypes.string,
  errors: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  property: PropTypes.object,
  properties: PropTypes.array,
};

PropertyForm.defaultProps = {
  className: undefined,
  errors: undefined,
  property: undefined,
};

export default PropertyForm;
